import authService from './authService';
import { ApiResponse } from '../types/common';
import type { ServiceInvoice, ServiceInvoiceListResult } from '../types/serviceInvoice';

/**
 * Service Invoice Service — Notas Fiscais de Serviço (Vendas)
 *
 * Endpoints em /financial/service-invoices (envelope {success, data|error}).
 * Criação/edição usam multipart/form-data (arquivo opcional: pdf/xml/png/jpg/jpeg, máx 10 MB).
 */
class ServiceInvoiceService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  /** Extrai a mensagem de erro do corpo da resposta (ou usa o fallback). */
  private async throwApiError(response: Response, fallback: string): Promise<never> {
    let message = fallback;
    try {
      const body = await response.json();
      message = body?.message || body?.error || fallback;
    } catch {
      /* corpo não-JSON — mantém fallback */
    }
    throw new Error(message);
  }

  /**
   * Envio multipart/form-data (POST/PUT) com fallback de autenticação
   * (cookie → token), seguindo o padrão de financialService.postMultipart.
   */
  private async sendMultipart(
    method: 'POST' | 'PUT',
    url: string,
    fd: FormData,
    fallbackError: string,
  ): Promise<Response> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}${url}`, {
        method,
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      await this.throwApiError(response, fallbackError);
    }
    return response;
  }

  /** GET /financial/service-invoices?year=YYYY[&month=M][&companyId=] */
  async list(year: number, month?: number, companyId?: number): Promise<ServiceInvoiceListResult> {
    const params = new URLSearchParams({ year: year.toString() });
    if (month != null) params.set('month', month.toString());
    if (companyId != null) params.set('companyId', companyId.toString());

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/service-invoices?${params}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao carregar notas fiscais de serviço');
    const data: ApiResponse<ServiceInvoiceListResult> = await response.json();
    return data.data ?? { invoices: [], total: 0 };
  }

  /** POST /financial/service-invoices (multipart) — 409 se (empresa, número) duplicado */
  async create(payload: FormData): Promise<ServiceInvoice> {
    const response = await this.sendMultipart(
      'POST',
      '/financial/service-invoices',
      payload,
      'Falha ao criar nota fiscal de serviço',
    );
    const data: ApiResponse<ServiceInvoice> = await response.json();
    return data.data!;
  }

  /** PUT /financial/service-invoices/:id (multipart) — arquivo opcional substitui o atual */
  async update(id: number, payload: FormData): Promise<ServiceInvoice> {
    const response = await this.sendMultipart(
      'PUT',
      `/financial/service-invoices/${id}`,
      payload,
      'Falha ao atualizar nota fiscal de serviço',
    );
    const data: ApiResponse<ServiceInvoice> = await response.json();
    return data.data!;
  }

  /** DELETE /financial/service-invoices/:id */
  async remove(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/financial/service-invoices/${id}`);
    if (!response.ok) await this.throwApiError(response, 'Falha ao excluir nota fiscal de serviço');
  }

  /**
   * GET /financial/service-invoices/:id/file — download binário via blob,
   * seguindo o padrão de financialService.downloadFile.
   */
  async downloadFile(id: number, fallbackFilename?: string | null): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const url = `/financial/service-invoices/${id}/file`;

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}${url}`, {
        method: 'GET',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      await this.throwApiError(response, 'Falha ao baixar o arquivo da nota fiscal de serviço');
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? fallbackFilename ?? `nota-servico-${id}`;

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(objectUrl);
  }
}

// Singleton
export const serviceInvoiceService = new ServiceInvoiceService();
export default serviceInvoiceService;
